/**
 * `JobScoreGuide` — guida contestuale ai punteggi delle professioni (Job e-CF
 * 3.0), mostrata in Dashboard ("Le tue professioni") e nella pagina dedicata.
 *
 * Analoga a `ScoreGuide` (competenze), ma un livello più in alto: le metriche di
 * un Job sono **derivate dai match sulle competenze** che il Job richiede, non
 * dagli assessment (i Job non hanno valutazioni proprie). Stesso pattern di
 * trigger e `Modal` del design system.
 */
import { useState } from 'react';
import { Button, Icon, Modal } from './ui';

export interface JobScoreGuideProps {
  /** Classe extra sul pulsante trigger (es. per l'allineamento nel titolo). */
  className?: string;
}

interface GuideEntry {
  term: string;
  description: string;
}

/**
 * Testi di dominio della guida ai Job. Chiariscono che i valori sono calcolati
 * dalle competenze possedute e come il Livello richiesto dalla professione entra
 * nel gate.
 */
const GUIDE_ENTRIES: readonly GuideEntry[] = [
  {
    term: 'Match',
    description:
      'Quanto il tuo profilo copre e soddisfa le competenze richieste dalla professione, pesate per importanza. Le competenze richieste che non possiedi contano come 0. È calcolato dai tuoi punteggi sulle competenze, con il gate del Livello richiesto dalla professione.',
  },
  {
    term: 'Copertura',
    description:
      'Quante delle competenze richieste dalla professione hai già iniziato a valutare (con almeno una skill valutata), pesate per la loro importanza. Misura l’ampiezza: conta quante competenze possiedi, non quanto sei bravo.',
  },
  {
    term: 'Proficiency',
    description:
      'La bravura media sulle sole competenze che hai valutato (pesata per importanza). Misura la profondità: le competenze mancanti non la abbassano. Se non hai valutato alcuna competenza della professione, non è calcolabile e appare come “—”.',
  },
  {
    term: 'Ranking',
    description:
      'Le professioni sono ordinate con un punteggio interno che combina i valori sopra e li pondera in base a quante competenze hai valutato: un ottimo risultato basato su pochissime competenze è meno affidabile e viene ridimensionato.',
  },
];

export function JobScoreGuide({ className }: JobScoreGuideProps) {
  const [open, setOpen] = useState(false);

  const triggerClasses = ['livello-guide__trigger', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <button
        type="button"
        className={triggerClasses}
        aria-label="Come funzionano i punteggi delle professioni"
        title="Come funzionano i punteggi delle professioni"
        onClick={() => setOpen(true)}
      >
        <Icon name="info" size={18} />
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Come funzionano i punteggi delle professioni"
        footer={
          <Button type="button" variant="primary" onClick={() => setOpen(false)}>
            Ho capito
          </Button>
        }
      >
        <p className="livello-guide__intro">
          Le professioni non si valutano direttamente: il tuo posizionamento è
          calcolato a partire dalle competenze che possiedi e dal Livello
          richiesto da ciascuna professione.
        </p>
        <ul className="livello-guide__list">
          {GUIDE_ENTRIES.map((entry) => (
            <li key={entry.term} className="livello-guide__item">
              <span className="livello-guide__badge">
                <span className="livello-guide__label">{entry.term}</span>
              </span>
              <span className="livello-guide__desc">{entry.description}</span>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}

export default JobScoreGuide;
